import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminShell } from "@/components/admin/AdminShell";
import { api, downloadBlob, postDownloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { OrdersSummaryCards } from "@/pages/admin/orders/OrdersSummaryCards";
import { CreditNotesTab } from "@/pages/admin/orders/CreditNotesTab";
import { OutstandingReportTab } from "@/pages/admin/orders/OutstandingReportTab";
import { OrdersFiltersBar } from "@/pages/admin/orders/OrdersFiltersBar";
import { BulkActionsToolbar } from "@/pages/admin/orders/BulkActionsToolbar";
import { OrdersTable } from "@/pages/admin/orders/OrdersTable";
import { InvoicesTable } from "@/pages/admin/orders/InvoicesTable";
import { RejectOrderDialog } from "@/pages/admin/orders/RejectOrderDialog";
import { RecordPaymentDialog } from "@/pages/admin/orders/RecordPaymentDialog";
import { ShipOrderDialog } from "@/pages/admin/orders/ShipOrderDialog";
import { EditInvoiceDialog } from "@/pages/admin/orders/EditInvoiceDialog";
import { EditOrderItemsDialog } from "@/pages/admin/orders/EditOrderItemsDialog";
import { ViewOrderSheet } from "@/pages/admin/orders/ViewOrderSheet";
import { DeleteInvoiceDialog } from "@/pages/admin/orders/DeleteInvoiceDialog";
import { NewOrderDialog } from "@/pages/admin/orders/NewOrderDialog";
import { KARAT_FACTORS, ORDER_TAB_STATUSES } from "@/pages/admin/orders/orderConstants";
import { hasAdminSession } from "@/lib/session";

export default function AdminOrders({ mode = "orders" }) {
  const navigate = useNavigate();
  const [topTab, setTopTab] = useState("invoices");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [shopFilter, setShopFilter] = useState("all");
  const [salesmanFilter, setSalesmanFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [staff, setStaff] = useState(null);
  const [summary, setSummary] = useState(null);
  const [shops, setShops] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [payCheque, setPayCheque] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payKind, setPayKind] = useState("inr");        // inr | metal
  const [payGoldWeight, setPayGoldWeight] = useState("");
  const [payGoldPurity, setPayGoldPurity] = useState("24K");
  const [payGoldFinePct, setPayGoldFinePct] = useState("");
  const [creditNotesForPartner, setCreditNotesForPartner] = useState([]);
  const [selectedCreditNoteId, setSelectedCreditNoteId] = useState(null);
  const [goldRate, setGoldRate] = useState(null);
  const [settleGoldInRupee, setSettleGoldInRupee] = useState(false);
  const [shipFor, setShipFor] = useState(null);
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editDueDate, setEditDueDate] = useState("");
  const [editTerms, setEditTerms] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [viewFor, setViewFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [editItemsFor, setEditItemsFor] = useState(null);
  const [editItemsDraft, setEditItemsDraft] = useState([]);

  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);

  const load = async () => {
    setOrdersLoading(true);
    const params = {};
    if (statusFilter !== "all" && statusFilter !== "in_progress") params.status = statusFilter;
    if (paymentFilter !== "all") params.payment_status = paymentFilter;
    if (invoiceStatusFilter !== "all") params.invoice_status = invoiceStatusFilter;
    if (overdueOnly) params.overdue = true;
    if (shopFilter !== "all") params.shop_id = shopFilter;
    if (salesmanFilter !== "all") params.salesman = salesmanFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (search.trim()) params.search = search.trim();
    try {
      const { data } = await api.get("/crm/orders", { params });
      setOrders(data);
    } catch { setOrders([]); }
    finally { setOrdersLoading(false); }
  };
  const loadSummary = () => api.get("/crm/orders/summary").then(({ data }) => setSummary(data)).catch(() => {});

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/crm/shops", { params: { status: "approved" } }).then(({ data }) => setShops(data)).catch(() => {});
    api.get("/crm/salesmen").then(({ data }) => setSalesmen(data)).catch(() => {});
    api.get("/rates/public").then(({ data }) => setGoldRate(data.gold_rate_per_gram_24k || 0)).catch(() => {});
    loadSummary();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); setSelectedIds([]); /* eslint-disable-next-line */ }, [statusFilter, paymentFilter, invoiceStatusFilter, overdueOnly, shopFilter, salesmanFilter, dateFrom, dateTo]);

  const runSearch = (e) => { e.preventDefault(); load(); };

  const canAct = (o) => !!staff && (staff.is_manager || staff.uid === o.salesman_id);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); load(); loadSummary(); }
    catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const approve = (o) => act(() => api.post(`/crm/orders/${o.id}/approve`), `Order ${o.order_no} approved`);
  const process = (o) => act(() => api.post(`/crm/orders/${o.id}/process`), `Order ${o.order_no} moved to Processing`);
  const complete = (o) => act(() => api.post(`/crm/orders/${o.id}/complete`), `Order ${o.order_no} completed`);
  const cancelOrder = (o) => act(() => api.post(`/crm/orders/${o.id}/cancel`), o.invoice_no ? "Invoice cancelled" : "Order cancelled");
  const generateInvoice = (o) => act(() => api.post(`/crm/orders/${o.id}/generate-invoice`), `Invoice generated for order ${o.order_no}`);
  const markSettled = (o) => act(() => api.post(`/crm/orders/${o.id}/settle`), `Invoice ${o.invoice_no} marked as fully settled`);
  const submitReject = () => act(async () => {
    await api.post(`/crm/orders/${rejectFor.id}/reject`, { reason: rejectReason });
    setRejectFor(null); setRejectReason("");
  }, "Order rejected");
  const resetPay = () => {
    setPayFor(null); setPayAmount(""); setPayRef(""); setPayCheque(""); setPayDate(""); setPayNote("");
    setPayKind("inr"); setPayGoldWeight(""); setPayGoldPurity("24K"); setPayGoldFinePct("");
    setCreditNotesForPartner([]); setSelectedCreditNoteId(null); setSettleGoldInRupee(false);
  };
  const openPay = (o) => {
    setPayFor(o);
    if (o?.shop_id) {
      api.get("/crm/credit-notes", { params: { shop_id: o.shop_id, active_only: true } })
        .then(({ data }) => setCreditNotesForPartner(data)).catch(() => setCreditNotesForPartner([]));
    }
  };
  const onSelectCreditNote = (val) => {
    if (val === "none") { setSelectedCreditNoteId(null); setPayAmount(""); return; }
    const cn = creditNotesForPartner.find((c) => c.id === val);
    setSelectedCreditNoteId(val);
    setSettleGoldInRupee(false);
    setPayAmount(cn ? String(cn.remaining_amount) : "");
  };
  const onToggleSettleGold = (checked) => {
    setSettleGoldInRupee(checked);
    if (checked) {
      setSelectedCreditNoteId(null);
      const goldOutstanding = Number(payFor?.gold_outstanding_fine_g || 0);
      const rupees = goldRate ? Math.round(goldOutstanding * goldRate * 100) / 100 : 0;
      setPayAmount(rupees ? String(rupees) : "");
    } else {
      setPayAmount("");
    }
  };
  const submitPayment = () => act(async () => {
    if (payKind === "metal") {
      await api.post(`/crm/orders/${payFor.id}/payments`, {
        payment_mode: "metal",
        gold_weight_g: Number(payGoldWeight),
        gold_purity: payGoldPurity,
        gold_purity_fine_pct: payGoldPurity === "Other" ? Number(payGoldFinePct) : 0,
        reference: payRef, payment_date: payDate || undefined, note: payNote,
      });
    } else if (settleGoldInRupee) {
      await api.post(`/crm/orders/${payFor.id}/payments`, {
        payment_mode: "gold_settle_via_rupee",
        amount: Number(payAmount), payment_date: payDate || undefined, note: payNote,
      });
    } else if (selectedCreditNoteId) {
      await api.post(`/crm/orders/${payFor.id}/payments`, {
        payment_mode: "credit_note", credit_note_id: selectedCreditNoteId,
        amount: Number(payAmount), payment_date: payDate || undefined, note: payNote,
      });
    } else {
      await api.post(`/crm/orders/${payFor.id}/payments`, {
        payment_mode: "inr",
        amount: Number(payAmount), mode: payMode, reference: payRef, cheque_number: payCheque,
        payment_date: payDate || undefined, note: payNote,
      });
    }
    resetPay();
  }, "Payment recorded");
  const goldFinePreview = (() => {
    const w = Number(payGoldWeight) || 0;
    const f = payGoldPurity === "Other" ? (Number(payGoldFinePct) || 0) / 1000 : (KARAT_FACTORS[payGoldPurity] || 0);
    return +(w * f).toFixed(3);
  })();
  const submitShip = () => act(async () => {
    await api.post(`/crm/orders/${shipFor.id}/ship`, { courier_name: courier, tracking_number: tracking });
    setShipFor(null); setCourier(""); setTracking("");
  }, "Order marked as shipped");
  const openEdit = (o) => { setEditFor(o); setEditDueDate(o.due_date || ""); setEditTerms(o.payment_terms_days ?? ""); setEditNotes(o.invoice_notes || ""); };
  const submitEdit = () => act(async () => {
    await api.put(`/crm/orders/${editFor.id}/invoice-meta`, {
      due_date: editDueDate || null, payment_terms_days: editTerms === "" ? null : Number(editTerms), invoice_notes: editNotes,
    });
    setEditFor(null);
  }, "Invoice updated");
  const submitDelete = () => act(async () => {
    await api.delete(`/crm/orders/${deleteFor.id}`);
    setDeleteFor(null);
  }, "Invoice permanently deleted");

  // ---- Edit Order (PENDING orders only): quantity, stone weight, labour/making charge ----
  const openEditItems = (o) => {
    const draft = (o.items || []).map((it) => {
      const ps = it.pricing_snapshot || {};
      const diamondTotal = (ps.diamonds || []).reduce((s, d) => s + (Number(d.carat) || 0), 0);
      const stoneTotal = (ps.colour_stones || []).reduce((s, d) => s + (Number(d.carat) || 0), 0);
      return {
        product_id: it.product_id,
        name: it.name,
        sku: it.sku,
        quantity: it.quantity,
        has_diamonds: (ps.diamonds || []).length > 0,
        has_stones: (ps.colour_stones || []).length > 0,
        has_gold: !!(ps.gold && ps.gold.weight_g > 0),
        diamond_carat_total: (ps.diamonds || []).length > 0 ? diamondTotal : null,
        stone_carat_total: (ps.colour_stones || []).length > 0 ? stoneTotal : null,
        making_value: ps.gold ? ps.gold.making_value : null,
        current_unit_price: it.unit_price,
        current_line_total: it.line_total,
      };
    });
    setEditItemsDraft(draft);
    setEditItemsFor(o);
  };
  const updateDraftField = (pid, field, value) => {
    setEditItemsDraft((prev) => prev.map((d) => (d.product_id === pid ? { ...d, [field]: value } : d)));
  };
  const submitEditItems = () => act(async () => {
    const items = editItemsDraft.map((d) => ({
      product_id: d.product_id,
      quantity: Math.max(1, Number(d.quantity) || 1),
      diamond_carat_total: d.has_diamonds ? Number(d.diamond_carat_total) : null,
      stone_carat_total: d.has_stones ? Number(d.stone_carat_total) : null,
      making_value: d.has_gold ? Number(d.making_value) : null,
    }));
    await api.put(`/crm/orders/${editItemsFor.id}/edit-items`, { items });
    setEditItemsFor(null); setEditItemsDraft([]);
  }, "Order updated");
  const draftEstimatedTotal = editItemsDraft.reduce((s, d) => s + (Number(d.current_unit_price) || 0) * Math.max(1, Number(d.quantity) || 1), 0);

  const printInvoice = (o) => window.open(`/crm/invoice/${o.id}?print=1`, "_blank");
  const downloadInvoice = (o) => downloadBlob(`/crm/orders/${o.id}/invoice.pdf`, `Invoice-${o.invoice_no || o.order_no}.pdf`).catch(() => toast.error("Download failed"));

  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds((prev) => prev.length === visibleOrders.length ? [] : visibleOrders.map((o) => o.id));
  const clearSelection = () => setSelectedIds([]);

  const bulkZip = () => postDownloadBlob("/crm/orders/bulk/invoices.zip", { order_ids: selectedIds }, "Invoices.zip")
    .then(() => toast.success(`${selectedIds.length} invoice(s) downloaded`)).catch(() => toast.error("Download failed"));
  const bulkCsv = () => postDownloadBlob("/crm/orders/bulk/export.csv", { order_ids: selectedIds }, "Orders-Export.csv")
    .then(() => toast.success("Export ready")).catch(() => toast.error("Export failed"));
  const bulkRemind = async () => {
    try {
      const { data } = await api.post("/crm/orders/bulk/remind", { order_ids: selectedIds });
      toast.success(`${data.reminders.length} reminder message(s) logged — copy & send via WhatsApp/SMS`);
    } catch { toast.error("Could not log reminders"); }
  };
  const bulkStatus = async (action) => {
    try {
      const { data } = await api.post("/crm/orders/bulk/status", { order_ids: selectedIds, action });
      toast.success(`${data.succeeded.length} order(s) updated${data.failed.length ? `, ${data.failed.length} skipped` : ""}`);
      load(); loadSummary(); clearSelection();
    } catch { toast.error("Bulk update failed"); }
  };

  const openNewOrder = () => {
    setNewOrderOpen(true);
    setSelectedShop(""); setOrderItems([]); setProductQuery("");
    if (allProducts.length === 0) api.get("/products").then(({ data }) => setAllProducts(Array.isArray(data) ? data : data.items || [])).catch(() => {});
  };
  const productMatches = productQuery
    ? allProducts.filter((p) => p.name.toLowerCase().includes(productQuery.toLowerCase()) || (p.sku || "").toLowerCase().includes(productQuery.toLowerCase())).slice(0, 8)
    : [];
  // Add a design line (or bump its qty). `barcode` is the physical piece tag that
  // was scanned, if any — kept on the line so staff can see which pieces they
  // pulled. Display-only for now: the order API takes product + quantity.
  const addItem = (p, barcode = null) => {
    setOrderItems((prev) => {
      const existing = prev.find((i) => i.product_id === p.id);
      if (existing) {
        return prev.map((i) => (i.product_id === p.id
          ? { ...i, quantity: i.quantity + 1, barcodes: barcode ? [...(i.barcodes || []), barcode] : (i.barcodes || []) }
          : i));
      }
      return [...prev, { product_id: p.id, name: p.name, sku: p.sku, price: p.price, quantity: 1, barcodes: barcode ? [barcode] : [] }];
    });
  };
  // Scanned code → line item. Tries the piece barcode first (QR on the tag),
  // falls back to matching the raw code against product SKUs so "type the SKU"
  // also works from a hand-held USB scanner. Returns false when nothing was added
  // so the dialog keeps the typed text for correction.
  const addByBarcode = async (code) => {
    const raw = (code || "").trim();
    if (!raw) return false;
    // The same physical piece can't go on an order twice.
    if (orderItems.some((i) => (i.barcodes || []).includes(raw))) {
      toast.warning(`${raw} is already on this order`);
      return true;
    }
    try {
      const { data } = await api.get(`/inventory/pieces/scan/${encodeURIComponent(raw)}`);
      const pid = data?.piece?.product_id;
      if (!pid) throw new Error("Piece has no product");
      if (data.piece?.status && data.piece.status !== "in_stock") {
        toast.error(`${raw} is ${String(data.piece.status).replace(/_/g, " ")} — not available to sell`);
        return false;
      }
      let product = allProducts.find((p) => p.id === pid);
      if (!product) {
        // Products list not loaded yet — fetch the specific one.
        try { product = (await api.get(`/products/${pid}`)).data; } catch (_e) { /* ignored */ }
      }
      if (!product) throw new Error("Product not found");
      addItem(product, data.piece?.barcode || raw);
      toast.success(`Added ${product.name} (${raw})`);
      return true;
    } catch (e) {
      const match = allProducts.find((p) => (p.sku || "").toLowerCase() === raw.toLowerCase());
      if (match) { addItem(match); toast.success(`Added by SKU: ${raw}`); return true; }
      toast.error(e.response?.data?.detail || `No piece or SKU found for "${raw}"`);
      return false;
    }
  };
  const updateQty = (pid, qty) => setOrderItems((prev) => prev.map((i) => (i.product_id === pid ? { ...i, quantity: Math.max(1, qty || 1) } : i)));
  const removeItem = (pid) => setOrderItems((prev) => prev.filter((i) => i.product_id !== pid));
  const estimatedTotal = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const submitNewOrder = async () => {
    if (!selectedShop) { toast.error("Select a shop"); return; }
    if (orderItems.length === 0) { toast.error("Add at least one product"); return; }
    setCreatingOrder(true);
    try {
      const { data } = await api.post("/crm/orders/create-for-shop", {
        shop_id: selectedShop, items: orderItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      toast.success(`Order ${data.order.order_no} created — pending approval`);
      setNewOrderOpen(false);
      load(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create order"); }
    finally { setCreatingOrder(false); }
  };

  // Orders tab = Pending/In Progress/Ready with NO invoice yet (invoicing is what moves a
  // record over to the Invoices tab). Invoices tab = any record with an invoice_no.
  // "in_progress" is a virtual (merged approved+processing) filter value that only exists
  // client-side — see ORDER_TAB_FILTER_OPTIONS in orderConstants.js.
  const visibleOrders = useMemo(() => {
    if (mode === "invoices") return orders.filter((o) => !!o.invoice_no);
    const base = orders.filter((o) => !o.invoice_no && ORDER_TAB_STATUSES.includes(o.status));
    if (statusFilter === "all") return base;
    if (statusFilter === "in_progress") return base.filter((o) => o.status === "approved" || o.status === "processing");
    return base.filter((o) => o.status === statusFilter);
  }, [orders, mode, statusFilter]);

  const allSelected = visibleOrders.length > 0 && selectedIds.length === visibleOrders.length;

  return (
    <AdminShell title={mode === "invoices" ? "Invoices" : "Orders"}>
      <OrdersSummaryCards data={summary} />

      {mode === "orders" ? (
        <>
          <OrdersFiltersBar
            mode="orders"
            search={search} setSearch={setSearch} onSearchSubmit={runSearch} onNewOrder={openNewOrder}
            shopFilter={shopFilter} setShopFilter={setShopFilter} shops={shops}
            salesmanFilter={salesmanFilter} setSalesmanFilter={setSalesmanFilter} salesmen={salesmen}
            paymentFilter={paymentFilter} setPaymentFilter={setPaymentFilter}
            invoiceStatusFilter={invoiceStatusFilter} setInvoiceStatusFilter={setInvoiceStatusFilter}
            dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
            overdueOnly={overdueOnly} setOverdueOnly={setOverdueOnly}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          />

          <BulkActionsToolbar
            selectedIds={selectedIds} onZip={bulkZip} onCsv={bulkCsv} onRemind={bulkRemind}
            onBulkStatus={bulkStatus} onClear={clearSelection}
          />

          <OrdersTable
            orders={visibleOrders} ordersLoading={ordersLoading} selectedIds={selectedIds}
            toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll} allSelected={allSelected}
            canAct={canAct} busy={busy} staff={staff} navigate={navigate}
            onApprove={approve} onProcess={process} onCancel={cancelOrder}
            onReject={setRejectFor} onShip={setShipFor} onView={setViewFor}
            onEditItems={openEditItems} onDelete={setDeleteFor} onGenerateInvoice={generateInvoice}
          />
        </>
      ) : (
        <Tabs value={topTab} onValueChange={setTopTab}>
          <TabsList className="mb-4">
            <TabsTrigger data-testid="top-tab-invoices" value="invoices">Invoices</TabsTrigger>
            <TabsTrigger data-testid="top-tab-credit-notes" value="credit_notes">Credit Notes &amp; Returns</TabsTrigger>
            <TabsTrigger data-testid="top-tab-outstanding" value="outstanding">Outstanding Report</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices">
            <OrdersFiltersBar
              mode="invoices"
              search={search} setSearch={setSearch} onSearchSubmit={runSearch}
              shopFilter={shopFilter} setShopFilter={setShopFilter} shops={shops}
              salesmanFilter={salesmanFilter} setSalesmanFilter={setSalesmanFilter} salesmen={salesmen}
              paymentFilter={paymentFilter} setPaymentFilter={setPaymentFilter}
              invoiceStatusFilter={invoiceStatusFilter} setInvoiceStatusFilter={setInvoiceStatusFilter}
              dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
              overdueOnly={overdueOnly} setOverdueOnly={setOverdueOnly}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            />

            <BulkActionsToolbar
              selectedIds={selectedIds} onZip={bulkZip} onCsv={bulkCsv} onRemind={bulkRemind}
              onBulkStatus={bulkStatus} onClear={clearSelection}
            />

            <InvoicesTable
              invoices={visibleOrders} ordersLoading={ordersLoading} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll} allSelected={allSelected}
              canAct={canAct} busy={busy} staff={staff} navigate={navigate}
              onCancel={cancelOrder} onView={setViewFor} onPay={openPay}
              onEditInvoice={openEdit} onDelete={setDeleteFor}
              onPrint={printInvoice} onDownload={downloadInvoice} onSettle={markSettled}
            />
          </TabsContent>

          <TabsContent value="credit_notes"><CreditNotesTab staff={staff} /></TabsContent>
          <TabsContent value="outstanding"><OutstandingReportTab shops={shops} salesmen={salesmen} /></TabsContent>
        </Tabs>
      )}
      <RejectOrderDialog rejectFor={rejectFor} onClose={() => setRejectFor(null)} rejectReason={rejectReason} setRejectReason={setRejectReason} busy={busy} onSubmit={submitReject} />

      <RecordPaymentDialog
        payFor={payFor} onClose={resetPay} payKind={payKind} setPayKind={setPayKind}
        payAmount={payAmount} setPayAmount={setPayAmount} payDate={payDate} setPayDate={setPayDate}
        payMode={payMode} setPayMode={setPayMode} payRef={payRef} setPayRef={setPayRef}
        payCheque={payCheque} setPayCheque={setPayCheque} payNote={payNote} setPayNote={setPayNote}
        payGoldWeight={payGoldWeight} setPayGoldWeight={setPayGoldWeight}
        payGoldPurity={payGoldPurity} setPayGoldPurity={setPayGoldPurity}
        payGoldFinePct={payGoldFinePct} setPayGoldFinePct={setPayGoldFinePct}
        goldFinePreview={goldFinePreview} staff={staff} busy={busy} onSubmit={submitPayment}
        creditNotes={creditNotesForPartner} selectedCreditNoteId={selectedCreditNoteId} onSelectCreditNote={onSelectCreditNote}
        goldRate={goldRate} settleGoldInRupee={settleGoldInRupee} onToggleSettleGold={onToggleSettleGold}
      />

      <ShipOrderDialog shipFor={shipFor} onClose={() => setShipFor(null)} courier={courier} setCourier={setCourier} tracking={tracking} setTracking={setTracking} busy={busy} onSubmit={submitShip} />

      <EditInvoiceDialog editFor={editFor} onClose={() => setEditFor(null)} editDueDate={editDueDate} setEditDueDate={setEditDueDate} editTerms={editTerms} setEditTerms={setEditTerms} editNotes={editNotes} setEditNotes={setEditNotes} busy={busy} onSubmit={submitEdit} />

      <EditOrderItemsDialog
        editItemsFor={editItemsFor} onClose={() => { setEditItemsFor(null); setEditItemsDraft([]); }}
        editItemsDraft={editItemsDraft} updateDraftField={updateDraftField}
        draftEstimatedTotal={draftEstimatedTotal} busy={busy} onSubmit={submitEditItems}
      />

      <ViewOrderSheet viewFor={viewFor} onClose={() => setViewFor(null)} onGenerateInvoice={generateInvoice} busy={busy} />

      <DeleteInvoiceDialog deleteFor={deleteFor} onClose={() => setDeleteFor(null)} onSubmit={submitDelete} />

      <NewOrderDialog
        open={newOrderOpen} onClose={() => setNewOrderOpen(false)} shops={shops}
        selectedShop={selectedShop} setSelectedShop={setSelectedShop}
        productQuery={productQuery} setProductQuery={setProductQuery} productMatches={productMatches}
        onAddItem={addItem} orderItems={orderItems} updateQty={updateQty} removeItem={removeItem}
        estimatedTotal={estimatedTotal} creatingOrder={creatingOrder} onSubmit={submitNewOrder}
        onScan={addByBarcode}
      />
    </AdminShell>
  );
}
